/**
 * GlobalAttributes Extension
 * Adds class and id attributes to block-level nodes so they survive
 * the code-view round-trip instead of being stripped by the schema.
 *
 * Data attributes are preserved on inline marks as well as blocks. Tiptap only
 * keeps attributes a node or mark declares, so a span carrying behaviour in
 * data-* survived as far as its class and lost everything else: the obfuscated
 * mailto spans on the marketing site came back as
 * `<span class="mailto-obfuscated">someone@example.com</span>`, which exposed
 * the address in the source and left the decoder nothing to rebuild the link
 * from. Anything that stores state in data-* has the same problem.
 */

import { Extension } from '@tiptap/core';

const BLOCK_TYPES = [
	'heading',
	'paragraph',
	'blockquote',
	'listItem',
	'bulletList',
	'orderedList',
	'codeBlock',
	'table',
	'tableRow',
	'tableCell',
	'tableHeader',
];

// The marks that wrap arbitrary inline HTML. Deliberately not bold/italic and
// friends: those are generated by the toolbar, never carry data-*, and giving
// them attributes would stop adjacent identical marks merging.
const INLINE_TYPES = ['inlineClass', 'inlineStyle', 'inlineElement', 'link'];

/**
 * Collects every data-* attribute into a single declared attribute. Tiptap has
 * no wildcard, so the whole set is stashed as JSON on one attribute and spread
 * back out on render.
 */
const dataAttrs = {
	default: null,
	parseHTML: (element) => {
		const data = {};
		for (const attr of element.attributes) {
			if (attr.name.startsWith('data-')) {
				data[attr.name] = attr.value;
			}
		}
		return Object.keys(data).length > 0 ? JSON.stringify(data) : null;
	},
	renderHTML: (attributes) => {
		if (!attributes.dataAttrs) return {};
		try {
			return JSON.parse(attributes.dataAttrs);
		} catch {
			return {};
		}
	},
};

const passthrough = (name) => ({
	default: null,
	parseHTML: (element) => element.getAttribute(name) || null,
	renderHTML: (attributes) => (attributes[name] ? { [name]: attributes[name] } : {}),
});

const GlobalAttributes = Extension.create({
	name: 'globalAttributes',

	addGlobalAttributes() {
		return [
			{
				types: BLOCK_TYPES,
				attributes: {
					class: passthrough('class'),
					id: passthrough('id'),
					dataAttrs,
				},
			},
			{
				types: INLINE_TYPES,
				attributes: {
					// title rides along because it is the visible tooltip on the
					// same spans that carry data-*, and losing it is the same
					// class of bug.
					title: passthrough('title'),
					dataAttrs,
				},
			},
		];
	},
});

export default GlobalAttributes;
